import { useState, type FormEvent } from 'react';
import { api, GalatApi, GalatJaringan } from '../lib/api';
import { useSesi } from '../state';

interface Terminal { id: string; kode: string; nama: string }
interface Outlet { id: string; nama: string; kode: string; terminals?: Terminal[] }
interface HasilMasuk {
  token: string;
  pengguna: { id: string; nama: string; peran: string };
  tenant: { id: string; nama: string; slug: string };
  outlets: Outlet[];
}

/**
 * Masuk, dua langkah.
 *
 * Langkah kedua ada karena satu pelajaran mahal: versi pertama meminta kasir
 * MENGETIK id terminal — UUID 36 karakter, di layar sentuh, sambil antrean
 * menunggu. Salah satu karakter saja menghasilkan galat kunci asing yang
 * tidak memberi tahu apa pun.
 *
 * Sekarang daftar terminal datang bersama jawaban login, dan kasir memilih.
 * Yang tidak bisa diketik salah tidak perlu divalidasi.
 */
export function Masuk() {
  const { simpanSesi } = useSesi();
  const [hasil, setHasil] = useState<HasilMasuk | null>(null);
  const [email, setEmail] = useState('');
  const [sandi, setSandi] = useState('');
  const [galat, setGalat] = useState<string | null>(null);
  const [sibuk, setSibuk] = useState(false);

  async function kirim(e: FormEvent) {
    e.preventDefault();
    setGalat(null);
    setSibuk(true);
    try {
      const h = await api.masuk(email.trim(), sandi) as HasilMasuk;
      if (!h.outlets?.length) {
        setGalat('Akun ini belum ditugaskan ke outlet mana pun. Minta pemilik menambahkannya di Back-office.');
        return;
      }
      setHasil(h);
    } catch (err) {
      if (err instanceof GalatJaringan) {
        setGalat('Tidak bisa menghubungi server. Masuk pertama kali butuh internet — sesudahnya kasir bisa bekerja offline.');
      } else if (err instanceof GalatApi) {
        setGalat(err.message);
      } else {
        setGalat('Terjadi kesalahan tak terduga.');
      }
    } finally {
      setSibuk(false);
    }
  }

  if (hasil) {
    return <PilihTerminal hasil={hasil} kembali={() => setHasil(null)}
                          mulai={simpanSesi} />;
  }

  return (
    <div className="tengah">
      <form className="kartu" onSubmit={kirim}>
        <h1>Kasir Santap</h1>
        <p className="ket">Masuk untuk memulai. Setelah ini, pergantian kasir cukup dengan PIN.</p>

        {galat && <div className="galat">{galat}</div>}

        <div className="isian">
          <label htmlFor="email">Email</label>
          <input id="email" type="email" value={email} required autoComplete="username"
                 inputMode="email" autoCapitalize="none"
                 onChange={(e) => setEmail(e.target.value)} placeholder="nama@restoran.id" />
        </div>
        <div className="isian">
          <label htmlFor="sandi">Kata sandi</label>
          <input id="sandi" type="password" value={sandi} required autoComplete="current-password"
                 onChange={(e) => setSandi(e.target.value)} />
        </div>

        <button className="tbl utama lebar" type="submit" disabled={sibuk}>
          {sibuk ? 'Menghubungkan…' : 'Lanjut'}
        </button>
      </form>
    </div>
  );
}

function PilihTerminal({
  hasil, kembali, mulai,
}: {
  hasil: HasilMasuk;
  kembali: () => void;
  mulai: (s: any) => Promise<void>;
}) {
  const [outletId, setOutletId] = useState(hasil.outlets[0]!.id);
  const outlet = hasil.outlets.find((o) => o.id === outletId)!;
  const terminals = outlet.terminals ?? [];
  const [terminalId, setTerminalId] = useState(terminals[0]?.id ?? '');

  const pilih = (id: string) => {
    setOutletId(id);
    const t = hasil.outlets.find((o) => o.id === id)?.terminals ?? [];
    setTerminalId(t[0]?.id ?? '');
  };

  async function lanjut() {
    const t = terminals.find((x) => x.id === terminalId);
    if (!t) return;
    await mulai({
      token: hasil.token,
      pengguna: hasil.pengguna,
      tenant: hasil.tenant,
      outletId: outlet.id,
      outletNama: outlet.nama,
      terminalId: t.id,
      terminalKode: t.kode,
    });
  }

  return (
    <div className="tengah">
      <div className="kartu">
        <h1>Pilih terminal</h1>
        <p className="ket">{hasil.tenant.nama} · {hasil.pengguna.nama}</p>

        {hasil.outlets.length > 1 && (
          <div className="isian">
            <label htmlFor="outlet">Outlet</label>
            <select id="outlet" value={outletId} onChange={(e) => pilih(e.target.value)}
                    style={{
                      width: '100%', padding: '11px 12px', borderRadius: 8,
                      border: '1px solid var(--line)', background: 'var(--panel-2)',
                      color: 'var(--ink)', fontSize: 15,
                    }}>
              {hasil.outlets.map((o) => <option key={o.id} value={o.id}>{o.nama}</option>)}
            </select>
          </div>
        )}

        {terminals.length === 0 ? (
          <>
            <div className="galat">
              Outlet <b>{outlet.nama}</b> belum punya terminal terdaftar.
            </div>
            <p style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5, margin: '0 0 16px' }}>
              Minta pemilik menambahkannya di Back-office → Pengaturan → Terminal.
              Satu terminal mewakili satu perangkat kasir, dan nomor struk dihitung
              per terminal — itulah yang mencegah dua kasir offline menghasilkan
              nomor yang sama.
            </p>
          </>
        ) : (
          <div className="isian">
            <label>Terminal di {outlet.nama}</label>
            <div style={{ display: 'grid', gap: 7 }}>
              {terminals.map((t) => {
                const aktif = t.id === terminalId;
                return (
                  <button key={t.id} type="button" onClick={() => setTerminalId(t.id)}
                          aria-pressed={aktif}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            padding: '13px 14px', borderRadius: 9, textAlign: 'left',
                            border: `1px solid ${aktif ? 'var(--primary)' : 'var(--line)'}`,
                            background: aktif ? 'var(--primary-soft)' : 'var(--panel-2)',
                          }}>
                    <span style={{
                      fontFamily: '"IBM Plex Mono",monospace', fontWeight: 800,
                      fontSize: 14, color: 'var(--primary)', minWidth: 34,
                    }}>{t.kode}</span>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{t.nama}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <button className="tbl utama lebar" disabled={!terminalId} onClick={lanjut}>
          Mulai di terminal {terminals.find((t) => t.id === terminalId)?.kode ?? ''}
        </button>
        <button className="tbl samar lebar" style={{ marginTop: 8 }} onClick={kembali}>
          Kembali
        </button>
      </div>
    </div>
  );
}
